import {
  ButtonStyleOptions,
  AlignmentOptions,
  OfferingListLayoutOptions,
  SettingsKeys,
  ImageAndTextRatioOptions,
  ImagePositionOptions,
  ImageResizeOptions,
  DockLocationOptions,
  ImageShapeOptions,
  MobileSettingsKeys,
} from '../../Shared/appKeys/SettingsKeys';

export interface OfferingListWidgetDisplayOptions {
  multiOfferingsDisplayOptions: MultiOfferingsDisplayOptions;
  offeringDisplayOptions: OfferingDisplayOptions;
}

export const MAX_FONT_SIZE_MEDIUM_BUTTON = 18;

export interface MultiOfferingsDisplayOptions {
  layout: OfferingListLayoutOptions;
  title: string;
  isTitleVisible: boolean;
  titleAlignment: AlignmentOptions;
  categoriesDisplayOptions: CategoriesDisplayOptions;
  spaceBetweenOfferings: number;
  stripSpaceBetweenOfferings: number;
  cardsSpacing: number;
  dividerWidth: number;
  isDividerVisible: boolean;
  maxItemsPerRow: number;
}

export interface CategoriesDisplayOptions {
  isCategoriesVisible: boolean;
  categoriesContentAlignment: AlignmentOptions;
  isCategoriesContentStretch: boolean;
}

export interface OfferingDisplayOptions {
  isOfferingTagLineVisible: boolean;
  isOfferingDividerVisible: boolean;
  isOfferingDurationVisible: boolean;
  isOfferingPriceVisible: boolean;
  isOfferingOfferedDaysVisible: boolean;
  isOfferingStartDateVisible: boolean;
  isOfferingBookButtonVisible: boolean;
  isOfferingMoreInfoLabelVisible: boolean;
  isOfferingImageVisible: boolean;
  isLargeButton: boolean;
  isSecondaryButton: boolean;
  offeringImageResize: ImageResizeOptions;
  offeringImageShape: ImageShapeOptions;
  offeringImageDockLocation: DockLocationOptions;
  offeringTextAlignment: AlignmentOptions;
  layout: OfferingListLayoutOptions;
  buttonStyle: ButtonStyleOptions;
  bookActionText: string;
  noBookActionText: string;
  pendingApprovalActionText: string;
  moreInfoLabelText: string;
  ratio: ImageAndTextRatioOptions;
  ratioFlipped: boolean;
  imagePosition: ImagePositionOptions;
}

export function getDisplayOptions(
  settingsData,
  isMobile = false,
): OfferingListWidgetDisplayOptions {
  settingsData = settingsData || {};
  return {
    multiOfferingsDisplayOptions: {
      layout: settingsData[SettingsKeys.OFFERING_LIST_LAYOUT],
      title: settingsData[SettingsKeys.MULTI_OFFERINGS_TITLE_TEXT],
      isTitleVisible: settingsData[SettingsKeys.DISPLAY_MULTI_OFFERINGS_TITLE],
      titleAlignment:
        settingsData[SettingsKeys.MULTI_OFFERINGS_TITLE_ALIGNMENT],
      categoriesDisplayOptions: {
        isCategoriesVisible: settingsData[SettingsKeys.DISPLAY_CATEGORIES],
        categoriesContentAlignment:
          settingsData[SettingsKeys.CATEGORY_ALIGNMENT],
        isCategoriesContentStretch:
          settingsData[SettingsKeys.FIT_CATEGORY_WIDTH],
      },
      spaceBetweenOfferings: settingsData[SettingsKeys.SPACE_BETWEEN_OFFERINGS],
      stripSpaceBetweenOfferings:
        settingsData[SettingsKeys.STRIP_SPACE_BETWEEN_OFFERINGS],
      cardsSpacing: isMobile
        ? settingsData[MobileSettingsKeys.MOBILE_CARDS_SPACING]
        : settingsData[SettingsKeys.CARDS_SPACING],
      dividerWidth: settingsData[SettingsKeys.SERVICE_DIVIDER_WIDTH],
      maxItemsPerRow: settingsData[SettingsKeys.LAYOUT_CARDS_PER_ROW],
      isDividerVisible: settingsData[SettingsKeys.DISPLAY_SERVICE_DIVIDER],
    },
    offeringDisplayOptions: {
      isOfferingTagLineVisible: settingsData[SettingsKeys.DISPLAY_TAG_LINE],
      isOfferingDividerVisible: settingsData[SettingsKeys.DISPLAY_DIVIDER],
      isOfferingDurationVisible: settingsData[SettingsKeys.DISPLAY_DURATION],
      isOfferingPriceVisible: settingsData[SettingsKeys.DISPLAY_PRICE],
      isOfferingOfferedDaysVisible:
        settingsData[SettingsKeys.DISPLAY_DAYS_OFFERED],
      isOfferingStartDateVisible: settingsData[SettingsKeys.DISPLAY_START_DATE],
      isOfferingBookButtonVisible: settingsData[SettingsKeys.DISPLAY_BUTTON],
      isOfferingMoreInfoLabelVisible:
        settingsData[SettingsKeys.DISPLAY_MORE_INFO_LABEL],
      isOfferingImageVisible: settingsData[SettingsKeys.DISPLAY_IMAGE],
      offeringImageResize:
        settingsData[
          isMobile
            ? MobileSettingsKeys.MOBILE_IMAGE_RESIZE_OPTION
            : SettingsKeys.IMAGE_RESIZE_OPTION
        ],
      offeringImageShape: settingsData[SettingsKeys.IMAGE_SHAPE_OPTION],
      offeringImageDockLocation:
        settingsData[
          isMobile
            ? MobileSettingsKeys.MOBILE_PIN_LOCATION
            : SettingsKeys.PIN_LOCATION
        ],
      isLargeButton: false,
      isSecondaryButton:
        settingsData[SettingsKeys.BUTTON_STYLE] ===
          ButtonStyleOptions.SQUARE_HOLE ||
        settingsData[SettingsKeys.BUTTON_STYLE] ===
          ButtonStyleOptions.CIRCLE_HOLE,
      offeringTextAlignment: settingsData[SettingsKeys.TEXT_ALIGNMENT],
      layout: settingsData[SettingsKeys.OFFERING_LIST_LAYOUT],
      buttonStyle: settingsData[SettingsKeys.BUTTON_STYLE],
      bookActionText: settingsData[SettingsKeys.BOOK_FLOW_ACTION_TEXT],
      noBookActionText: settingsData[SettingsKeys.NO_BOOK_FLOW_ACTION_TEXT],
      pendingApprovalActionText:
        settingsData[SettingsKeys.PENDING_APPROVAL_FLOW_ACTION_TEXT],
      moreInfoLabelText: settingsData[SettingsKeys.MORE_INFO_LABEL_TEXT],
      ratio: settingsData[SettingsKeys.IMAGE_AND_TEXT_RATIO],
      ratioFlipped: settingsData[SettingsKeys.IMAGE_AND_TEXT_RATIO_IS_FLIPED],
      imagePosition: settingsData[SettingsKeys.IMAGE_POSITION_OPTION],
    },
  };
}
